import React, { useEffect, useState, useRef } from 'react';
import SimpleRichTextEditor from './SummerNoteEditor.jsx';
import PhotoAlbumUploader from './PhotoAlbumUploader';

// Style pour l'input file avec la charte graphique
const fileInputStyle = `
  input[type="file"]::file-selector-button {
    background-color: rgb(246, 240, 232);
    color: rgb(196, 150, 96);
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 0.25rem;
    font-weight: 600;
    cursor: pointer;
    margin-right: 1rem;
  }
  input[type="file"]::file-selector-button:hover {
    background-color: rgb(236, 230, 222);
  }
`;

export default function EventForm({ eventId = null, onCancel, onSaved }) {
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    location: '',
    start_datetime: '',
    end_datetime: '',
    is_public: true,
    member_only: false,
    global_quota: '',
    tariffs: [{ id: '', label: 'Tarif', price: 0 }],
    cover_image_url: '',
    photo_album: [],
    bank_account_info: ''
  });

  // Convertir une date ISO en format datetime-local (YYYY-MM-DDTHH:MM)
  // On extrait directement la date/heure sans conversion de timezone
  // car on stocke l'heure "telle quelle" (pas de vraie gestion UTC)
  const isoToLocalDatetime = (isoString) => {
    if (!isoString) return '';
    // Extraire YYYY-MM-DDTHH:MM directement du string ISO
    // Format attendu: "2026-01-26T12:00:00.000Z" ou "2026-01-26T12:00:00"
    const match = isoString.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/);
    if (match) {
      return match[1];
    }
    // Fallback si format différent
    const date = new Date(isoString);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const loadEvent = async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const url = new URL('/api/admin/events', API_BASE);
      const headers = { 'Content-Type': 'application/json' };
      const token = localStorage.getItem('authToken');
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(url.toString(), { headers });
      if (!res.ok) throw new Error('Erreur chargement événement');
      const data = await res.json();
      const ev = (data.events || []).find(e => e.id === eventId);
      if (!ev) throw new Error('Événement introuvable');
      setForm({
        title: ev.title || '',
        description: ev.description || '',
        location: ev.location || '',
        start_datetime: isoToLocalDatetime(ev.start_datetime),
        end_datetime: isoToLocalDatetime(ev.end_datetime),
        is_public: !!ev.is_public,
        member_only: !!ev.member_only,
        global_quota: ev.global_quota ?? '',
        tariffs: Array.isArray(ev.tariffs) ? ev.tariffs.map(t => ({ id: t.id, label: t.label, price: t.price })) : [{ id:'', label:'Tarif', price:0 }],
        cover_image_url: ev.cover_image_url || '',
        photo_album: ev.photo_album || [],
        bank_account_info: ev.bank_account_info || ''
      });
    } catch (e) { alert(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadEvent(); // eslint-disable-next-line
  }, [eventId]);

  const onChange = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const updateTariff = (i, k, v) => setForm(prev => ({ ...prev, tariffs: prev.tariffs.map((t, idx) => idx===i ? { ...t, [k]: v } : t) }));
  const addTariff = () => setForm(prev => ({ ...prev, tariffs: [...prev.tariffs, { id:'', label:'Tarif', price: 0 }] }));
  const removeTariff = (i) => setForm(prev => ({ ...prev, tariffs: prev.tariffs.filter((_, idx) => idx!==i) }));

  const uploadCover = async (file) => {
    try {
      const token = localStorage.getItem('authToken');
      const fd = new FormData();
      fd.append('file', file);  // Backend attend 'file' pas 'image'
      const url = API_BASE ? new URL('/api/admin/upload-image', API_BASE).toString() : '/api/admin/upload-image';
      const res = await fetch(url, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`}, body: fd });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        console.error('Upload error response:', errorData);
        
        // Gérer les différents formats d'erreur
        let errorMessage = 'Upload image échoué';
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          // Erreur Pydantic
          errorMessage = errorData.detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
        
        throw new Error(errorMessage);
      }
      const data = await res.json();
      onChange('cover_image_url', data.url || data.file_url);
      // Ne plus afficher d'alert - l'aperçu de l'image suffit
    } catch (e) { 
      console.error('Upload error:', e);
      alert(e.message); 
    }
  };

  const removeCover = () => {
    onChange('cover_image_url', null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Convertir datetime-local (YYYY-MM-DDTHH:MM) en ISO string sans décalage de timezone
  // L'heure saisie par l'utilisateur est l'heure qu'on veut stocker
  const localDatetimeToISO = (localDatetime) => {
    if (!localDatetime) return new Date().toISOString();
    // Ajouter les secondes et le Z pour indiquer UTC
    // On traite l'heure locale comme si c'était UTC pour éviter les décalages
    return `${localDatetime}:00.000Z`;
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const payload = {
        title: form.title.trim(),
        description: form.description || '',
        location: form.location || '',
        start_datetime: localDatetimeToISO(form.start_datetime),
        end_datetime: localDatetimeToISO(form.end_datetime),
        is_public: !!form.is_public,
        member_only: !!form.member_only,
        global_quota: form.global_quota === '' ? null : Number(form.global_quota),
        tariffs: (form.tariffs || []).map(t => ({ id: t.id || undefined, label: t.label || 'Tarif', price: Number(t.price || 0) })),
        cover_image_url: form.cover_image_url || null,
        photo_album: form.photo_album || [],
        bank_account_info: form.bank_account_info || null
      };
      
      console.log('📤 Payload événement:', payload);
      console.log('📸 Photo album à sauvegarder:', form.photo_album);
      const url = eventId
        ? (API_BASE ? new URL(`/api/admin/events/${eventId}`, API_BASE).toString() : `/api/admin/events/${eventId}`)
        : (API_BASE ? new URL('/api/admin/events', API_BASE).toString() : '/api/admin/events');
      const method = eventId ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error('Erreur sauvegarde');
      onSaved && onSaved();
    } catch (e) { alert(e.message); }
    finally { setLoading(false); }
  };

  return (
    <>
      <style>{fileInputStyle}</style>
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">{eventId ? 'Modifier l\'événement' : 'Nouvel événement'}</h2>
          <button onClick={onCancel} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>
        <form onSubmit={save} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
          <input value={form.title} onChange={(e)=>onChange('title', e.target.value)} className="w-full px-3 py-2 border rounded" required />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <SimpleRichTextEditor value={form.description} onChange={(html)=>onChange('description', html)} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Image de couverture</label>
          
          {/* Aperçu de l'image si elle existe */}
          {form.cover_image_url && (
            <div className="mb-3 p-3 border rounded-lg bg-gray-50">
              <div className="flex items-start gap-3">
                <img 
                  src={`${API_BASE}${form.cover_image_url}`} 
                  alt="Aperçu" 
                  className="w-32 h-32 object-cover rounded border"
                  onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128"><rect width="128" height="128" fill="%23ddd"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%23999">Image</text></svg>'; }}
                />
                <div className="flex-1">
                  <p className="text-sm text-gray-600 mb-2">Image actuelle</p>
                  <div className="flex gap-2">
                    <a 
                      href={`${API_BASE}${form.cover_image_url}`} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="px-3 py-1 text-sm text-white rounded"
                      style={{ backgroundColor: 'rgb(196, 150, 96)' }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(176, 130, 76)'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'rgb(196, 150, 96)'}
                    >
                      Voir l'image
                    </a>
                    <button 
                      type="button"
                      onClick={removeCover}
                      className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Input pour uploader une nouvelle image */}
          <div className="flex items-center gap-3">
            <input 
              ref={fileInputRef} 
              type="file" 
              accept="image/*" 
              onChange={(e)=> e.target.files && e.target.files[0] && uploadCover(e.target.files[0])} 
              className="block w-full text-sm text-gray-500"
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">Formats acceptés: JPG, PNG, WEBP (max 5MB)</p>
        </div>

        {/* Album Photo */}
        <div>
          <PhotoAlbumUploader
            photos={form.photo_album}
            onPhotosChange={(photos) => onChange('photo_album', photos)}
            maxPhotos={36}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Début *</label>
            <input type="datetime-local" value={form.start_datetime} onChange={(e)=>onChange('start_datetime', e.target.value)} className="w-full px-3 py-2 border rounded" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fin *</label>
            <input type="datetime-local" value={form.end_datetime} onChange={(e)=>onChange('end_datetime', e.target.value)} className="w-full px-3 py-2 border rounded" required />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Lieu</label>
          <input value={form.location} onChange={(e)=>onChange('location', e.target.value)} className="w-full px-3 py-2 border rounded" />
        </div>
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_public} onChange={(e)=>onChange('is_public', e.target.checked)} /> Public</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.member_only} onChange={(e)=>onChange('member_only', e.target.checked)} /> Réservé aux membres</label>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quota global (optionnel)</label>
            <input type="number" min="0" value={form.global_quota} onChange={(e)=>onChange('global_quota', e.target.value)} className="w-40 px-3 py-2 border rounded" placeholder="illimité" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Numéro de compte bancaire pour virement</label>
          <input 
            value={form.bank_account_info} 
            onChange={(e)=>onChange('bank_account_info', e.target.value)} 
            className="w-full px-3 py-2 border rounded" 
            placeholder="BE00 0000 0000 0000"
          />
          <p className="text-xs text-gray-500 mt-1">Ce numéro sera affiché aux participants qui choisissent le paiement par virement</p>
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">Tarifs</label>
            <button type="button" onClick={addTariff} className="px-3 py-1 border rounded">Ajouter un tarif</button>
          </div>
          <div className="space-y-2">
            {form.tariffs.map((t, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                <div className="md:col-span-7">
                  <input value={t.label} onChange={(e)=>updateTariff(i,'label',e.target.value)} className="w-full px-3 py-2 border rounded" placeholder="Label (ex: Adulte)" />
                </div>
                <div className="md:col-span-3">
                  <input type="number" step="0.01" min="0" value={t.price} onChange={(e)=>updateTariff(i,'price',e.target.value)} className="w-full px-3 py-2 border rounded" placeholder="Prix EUR" />
                </div>
                <div className="md:col-span-2 text-right">
                  <button type="button" onClick={()=>removeTariff(i)} className="px-3 py-2 border rounded text-red-600">Supprimer</button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex gap-3 pt-2">
          <button 
            type="submit" 
            className="text-white px-6 py-2 rounded" 
            style={{ backgroundColor: 'rgb(196, 150, 96)' }}
            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(176, 130, 76)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'rgb(196, 150, 96)'}
            disabled={loading}
          >
            {eventId ? 'Mettre à jour' : 'Créer l\'événement'}
          </button>
          <button type="button" onClick={onCancel} className="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600">Annuler</button>
        </div>
      </form>
    </div>
    </>
  );
}